import {
  GaussianBlurBackgroundProcessor,
  VirtualBackgroundProcessor,
  isSupported,
} from '@twilio/video-processors';
import {
  VideoEffect,
  VideoEffectProcessor,
} from '../../../../shared/typescript/sb-lib/webrtc/client';
import { LocalVideoTrack } from 'sb-lib/webrtc/twilio-video';
import { getImage } from 'shared/api';

export class TwilioVideoProcessor implements VideoEffectProcessor {
  #tracks: Record<string, LocalVideoTrack[]> = {};
  #processor: null | GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor = null;

  async prepare(effect: null | VideoEffect, backgroundImageId?: string) {
    Object.entries(this.#tracks).forEach(e => {
      const tracks = e[1];
      tracks.forEach(t => (t.processor ? t.removeProcessor(t.processor) : null));
    });

    if (effect === VideoEffect.lightBlur) {
      await this.#makeGaussianBlurProcessor(10, 5);
    } else if (effect === VideoEffect.strongBlur) {
      await this.#makeGaussianBlurProcessor(10, 15);
    } else if (effect === VideoEffect.image && backgroundImageId) {
      await this.#makeVirtualBackgroundProcessor(backgroundImageId);
    } else {
      this.#processor = null;
    }
  }

  static isSupported() {
    return isSupported;
  }

  apply(stream: MediaStream): MediaStream {
    if (!TwilioVideoProcessor.isSupported()) {
      return stream;
    }

    const videoTracks = stream.getVideoTracks();

    videoTracks.forEach(track => {
      const twilioTrack = new LocalVideoTrack(track);

      if (!this.#tracks[stream.id]) {
        this.#tracks[stream.id] = [];
      }
      this.#tracks[stream.id].push(twilioTrack);

      if (this.#processor) {
        const video = document.createElement('video');
        twilioTrack.attach(video);
        twilioTrack.addProcessor(this.#processor);
        stream.removeTrack(track);
        stream.addTrack(twilioTrack.processedTrack as MediaStreamTrack);
      }
    });

    return stream;
  }

  async #makeGaussianBlurProcessor(maskBlurRadius: number, blurFilterRadius: number) {
    const processor = new GaussianBlurBackgroundProcessor({
      assetsPath: '/twilio_video_processors',
      maskBlurRadius: maskBlurRadius,
      blurFilterRadius: blurFilterRadius,
    });
    await processor.loadModel();
    this.#processor = processor;
  }

  stop(stream: MediaStream): void {
    if (!this.#tracks[stream.id]) {
      return;
    }

    this.#tracks[stream.id].forEach(t => t.stop());
  }

  async #makeVirtualBackgroundProcessor(backgroundImageId: string) {
    const munchedImage = await getImage(backgroundImageId, true);

    return new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.src = munchedImage.data.signedDownloadUrl;
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const processor = new VirtualBackgroundProcessor({
          assetsPath: '/twilio_video_processors',
          backgroundImage: img,
          maskBlurRadius: 10,
        });
        processor.loadModel().then(() => {
          this.#processor = processor;
          resolve();
        });
      };

      img.onerror = reject;
    });
  }
}
